import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  CopyableHash,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Pagination,
  SkeletonTableRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@shared/ui';
import type { Withdrawal } from '@entities/withdrawal';
import { isWithdrawalCancellable } from '@entities/withdrawal';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { formatDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { WithdrawalStatusBadge } from '@entities/withdrawal';
import { WithdrawalMeta } from '@entities/withdrawal';
import { MerchantWithdrawalSheet } from './merchant-withdrawal-sheet';

interface WithdrawalsTableProps {
  withdrawals: Withdrawal[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  isLoading?: boolean;
  isRefreshing?: boolean;
  isActionLoading?: boolean;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
  onCancel?: (id: string) => Promise<{ success: boolean; error?: string }>;
  openId?: string | null;
  loadError?: boolean;
}

export function WithdrawalsTable({
  withdrawals,
  pagination,
  isLoading = false,
  isRefreshing = false,
  isActionLoading = false,
  onRefresh,
  onPageChange,
  onCancel,
  openId = null,
  loadError = false,
}: WithdrawalsTableProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { copy } = useCopyToClipboard();
  const showSkeleton = (isLoading && !withdrawals.length) || isRefreshing;
  const spinRefresh = isLoading || isRefreshing;
  const [cancelTarget, setCancelTarget] = useState<Withdrawal | null>(null);
  const [openedId, setOpenedId] = useState<string | null>(openId);

  useEffect(() => {
    if (openId) setOpenedId(openId);
  }, [openId]);

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !onCancel) return;

    const result = await onCancel(cancelTarget.id);
    if (result.success) {
      toast(t('withdrawals.actions.cancelSuccess'), 'success');
      setCancelTarget(null);
      onRefresh?.();
      return;
    }

    onRefresh?.();
    setOpenedId(cancelTarget.id);
    setCancelTarget(null);
    if (result.error) {
      toast(result.error, 'error');
    }
  };

  return (
    <>
      <div className="surface-card">
        <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
          <h2 className="section-title">{t('withdrawals.userTitle')}</h2>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className="rounded-full sm:size-10"
            loading={spinRefresh}
            leftIcon={<RefreshCw className="size-4" />}
            aria-label={t('common.retry', { ns: 'common' })}
            onClick={() => onRefresh?.()}
          />
        </div>

        {showSkeleton ? (
          <SkeletonTableRows rows={6} className="gap-3" />
        ) : loadError && !withdrawals.length ? (
          <div className="space-y-3 py-12 text-center">
            <p className="font-sans text-sm text-muted-foreground">{t('withdrawals.loadError')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => onRefresh?.()}>
              {t('common.retry', { ns: 'common' })}
            </Button>
          </div>
        ) : !withdrawals.length ? (
          <div className="py-12 text-center font-sans text-xs text-muted-foreground sm:py-16 sm:text-sm">{t('withdrawals.empty')}</div>
        ) : (
          <>
            <div className="-mx-1 min-w-0 overflow-x-auto px-1">
              <Table className="table-compact min-w-[44rem]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">{t('withdrawals.columns.id')}</TableHead>
                    <TableHead className="w-[1%] whitespace-nowrap">{t('withdrawals.columns.network')}</TableHead>
                    <TableHead className="min-w-[8rem]">{t('withdrawals.columns.address')}</TableHead>
                    <TableHead className="min-w-[10.5rem] whitespace-nowrap text-right sm:min-w-[12rem]">
                      {t('withdrawals.columns.amount')}
                    </TableHead>
                    <TableHead>{t('withdrawals.columns.status')}</TableHead>
                    <TableHead>{t('withdrawals.columns.policy')}</TableHead>
                    <TableHead>{t('withdrawals.columns.date')}</TableHead>
                    <TableHead className="w-28">{t('withdrawals.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((withdrawal) => (
                    <TableRow key={withdrawal.id} className="cursor-pointer" onClick={() => setOpenedId(withdrawal.id)}>
                      <TableCell className="font-medium">{withdrawal.id}</TableCell>
                      <TableCell>{withdrawal.network}</TableCell>
                      <TableCell className="max-w-[10rem] sm:max-w-[12rem]">
                        <CopyableHash
                          value={withdrawal.address}
                          onCopy={() => copy(withdrawal.address, undefined, withdrawal.address)}
                        />
                      </TableCell>
                      <TableCell className="min-w-[10.5rem] whitespace-nowrap text-right font-medium tabular-nums sm:min-w-[12rem]">
                        {formatLedgerAmount(withdrawal.amount, { assetLabel: withdrawal.assetCode })}
                      </TableCell>
                      <TableCell>
                        <WithdrawalStatusBadge status={withdrawal.status} state={withdrawal.state} />
                      </TableCell>
                      <TableCell>
                        <WithdrawalMeta withdrawal={withdrawal} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(withdrawal.createdAt)}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        {isWithdrawalCancellable(withdrawal.status, withdrawal.state) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            disabled={isActionLoading}
                            onClick={() => setCancelTarget(withdrawal)}
                          >
                            {t('withdrawals.actions.cancel')}
                          </Button>
                        ) : (
                          <span
                            className="inline-flex min-h-8 items-center font-sans text-sm text-muted-foreground"
                            title={t('withdrawals.actions.unavailableHint')}
                          >
                            {t('withdrawals.actions.none')}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              onPageChange={(page) => onPageChange?.(page)}
            />
          </>
        )}
      </div>

      <MerchantWithdrawalSheet
        withdrawalId={openedId}
        fallback={withdrawals.find((item) => item.id === openedId) ?? null}
        onOpenChange={(next) => {
          if (!next) setOpenedId(null);
        }}
        onCancel={onCancel}
        onRefresh={onRefresh}
      />

      <Dialog open={Boolean(cancelTarget)} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('withdrawals.actions.cancelConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('withdrawals.actions.cancelConfirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              {t('withdrawals.actions.back')}
            </Button>
            <Button variant="destructive" loading={isActionLoading} onClick={() => void handleConfirmCancel()}>
              {t('withdrawals.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
